import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../../utils/httpRequest";
import { useSession } from "../../session/SessionProvider";
import { toast } from "react-toastify";
import { Search, Filter, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function OrgSupportPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");
  const navigate = useNavigate();
  const { user } = useSession();

  const token = user?.token;

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");

  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const limit = 20;

  const fetchTickets = async (_page = page) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {
        page: _page,
        limit,
        q,
        status,
        category,
        priority,
      };

      // ✅ Endpoint ادمین: باید در بک‌اند بسازی
      // GET /ticket/admin/list
      const res = await httpRequest("GET", "/ticket", params, {
        "x-access-token": token,
      });

      const payload = res?.data?.data || {};
      const list = Array.isArray(payload.items) ? payload.items : [];

      setTickets(list);
      setTotal(payload.total || list.length || 0);
      setPages(payload.pages || 1);
      setPage(payload.page || _page);
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets(1);
  }, [q, status, category, priority]);

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  } space-y-4`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("admin_support.page_title", "پشتیبانی (ادمین)")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />

        <main className={mainSectionClass}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Shield size={20} />
              {t("admin_support.page_header", "مدیریت تیکت‌ها")}
            </h1>

            <div className="text-sm text-gray-500">
              {t("admin_support.total", "کل")} : {total}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border p-4 flex flex-col md:flex-row gap-3 items-center">
            <div className="flex items-center gap-2 w-full md:w-1/3">
              <Search size={18} className="text-gray-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("admin_support.search_placeholder", "جستجو...")}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full md:w-1/6 border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">{t("admin_support.all_status", "همه وضعیت‌ها")}</option>
              <option value="open">{t("ticket.status_open", "باز")}</option>
              <option value="answered">{t("ticket.status_answered", "پاسخ داده شده")}</option>
              <option value="closed">{t("ticket.status_closed", "بسته شده")}</option>
            </select>

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full md:w-1/6 border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">{t("admin_support.all_categories", "همه دسته‌ها")}</option>
              <option value="billing">{t("ticket.category_billing", "مالی")}</option>
              <option value="technical">{t("ticket.category_technical", "فنی")}</option>
              <option value="content">{t("ticket.category_content", "محتوا")}</option>
              <option value="other">{t("ticket.category_other", "سایر")}</option>
            </select>

            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full md:w-1/6 border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">{t("admin_support.all_priorities", "همه اولویت‌ها")}</option>
              <option value="low">{t("ticket.priority_low", "کم")}</option>
              <option value="normal">{t("ticket.priority_normal", "معمولی")}</option>
              <option value="high">{t("ticket.priority_high", "بالا")}</option>
              <option value="urgent">{t("ticket.priority_urgent", "فوری")}</option>
            </select>

            <button
              onClick={() => fetchTickets(1)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700"
            >
              <Filter size={16} className="inline-block me-1" />
              {t("common.apply", "اعمال")}
            </button>
          </div>

          {/* List */}
          <div className="bg-white rounded-xl border overflow-hidden">
            {loading ? (
              <div className="p-6 text-sm text-gray-500">
                {t("common.loading", "در حال بارگذاری...")}
              </div>
            ) : tickets.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">
                {t("admin_support.empty", "تیکتی یافت نشد")}
              </div>
            ) : (
              <div className="divide-y">
                {tickets.map((tk) => (
                  <button
                    key={tk._id}
                    onClick={() => navigate(`/organization/support/${tk._id}`)}
                    className="w-full text-start p-4 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-semibold text-gray-800 line-clamp-1">
                          {tk.subject}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {tk.userName || tk.user?.name || tk.user?.email} •{" "}
                          {tk.createdAt}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-sm px-2 py-1 rounded bg-gray-100 text-gray-700">
                          {tk.category}
                        </span>
                        <span className="text-sm px-2 py-1 rounded bg-orange-50 text-orange-700">
                          {tk.priority}
                        </span>
                        {console.log(tk.status)}
                        <span
                          className={`text-sm px-2 py-1 rounded ${
                            tk.status === "open"
                              ? "bg-emerald-50 text-emerald-700"
                              : tk.status === "answered"
                              ? "bg-blue-50 text-blue-700"
                              : "bg-gray-200 text-gray-700"
                          }`}
                        >
                          {tk.status}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => fetchTickets(page - 1)}
                className="px-3 py-1 rounded border bg-white disabled:opacity-50"
              >
                {t("common.prev", "قبلی")}
              </button>

              <span className="text-sm text-gray-600">
                {page} / {pages}
              </span>

              <button
                disabled={page >= pages}
                onClick={() => fetchTickets(page + 1)}
                className="px-3 py-1 rounded border bg-white disabled:opacity-50"
              >
                {t("common.next", "بعدی")}
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
